import React from "react";
import Link from "next/link";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocNavFooter,
} from "@/components/docs";
import { GlobeAltIcon, ServerIcon, WifiIcon } from "@heroicons/react/24/outline";
import { docsColors, docsComponents } from "@/lib/docs-theme";

const monitorTypes = [
  {
    title: "HTTP/HTTPS Monitoring",
    description: "Monitor websites, APIs, and web services with advanced options",
    icon: GlobeAltIcon,
    href: "/docs/monitors/http",
    features: [
      "Custom request methods (GET, POST, etc.)",
      "Request headers and body",
      "Response validation",
      "SSL certificate expiration monitoring",
      "Follow redirects",
      "Authentication support",
    ],
  },
  {
    title: "TCP Port Monitoring",
    description: "Check if specific ports are open and accepting connections",
    icon: ServerIcon,
    href: "/docs/monitors/tcp",
    features: [
      "Any TCP port (1-65535)",
      "Connection timeout settings",
      "Database servers",
      "Mail servers",
      "Custom services",
      "Game servers",
    ],
  },
  {
    title: "Ping Monitoring (ICMP)",
    description: "Monitor server availability using ICMP ping",
    icon: WifiIcon,
    href: "/docs/monitors/ping",
    features: [
      "Basic connectivity check",
      "Response time monitoring",
      "Packet loss detection",
      "Network latency tracking",
      "Server uptime monitoring",
      "Infrastructure monitoring",
    ],
  },
];

export default function MonitorsOverviewPage() {
  const breadcrumbs = [{ label: "Documentation", href: "/docs" }, { label: "Monitor Types" }];

  return (
    <DocLayout
      title="Monitor Types - Uptime Monitor Documentation"
      description="Explore different monitoring options available in Uptime Monitor: HTTP/HTTPS, TCP, Ping, and SSL certificate monitoring."
      canonical="https://uptime-monitor.io/docs/monitors"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Monitor Types"
        description="Choose the right monitoring type for your specific needs. Each type offers unique features and configuration options."
      />

      {/* Common Features */}
      <DocSection>
        <DocAlert variant="info" className="p-6">
          <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
            Common Features Across All Monitor Types
          </h2>
          <ul className={`grid sm:grid-cols-2 gap-2 ${docsColors.text.body}`}>
            <li className="flex items-center">
              <span className="text-blue-400 mr-2">✓</span>
              Multi-region monitoring (15 locations)
            </li>
            <li className="flex items-center">
              <span className="text-blue-400 mr-2">✓</span>
              Configurable check frequency (10s - 60min)
            </li>
            <li className="flex items-center">
              <span className="text-blue-400 mr-2">✓</span>
              Instant notifications on status changes
            </li>
            <li className="flex items-center">
              <span className="text-blue-400 mr-2">✓</span>
              Historical uptime and response time data
            </li>
          </ul>
        </DocAlert>
      </DocSection>

      {/* Monitor Types Grid */}
      <DocSection>
        <div className="grid gap-6">
          {monitorTypes.map((type) => (
            <DocCard key={type.href}>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <type.icon className="h-8 w-8 text-blue-500" />
                </div>
                <div className="ml-4 flex-1">
                  <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-2`}>
                    {type.title}
                  </h2>
                  <p className={`${docsColors.text.body} mb-4`}>{type.description}</p>

                  <div className="mb-4">
                    <h3
                      className={`text-sm font-semibold ${docsColors.text.muted} uppercase tracking-wider mb-2`}
                    >
                      Key Features
                    </h3>
                    <ul className={`grid sm:grid-cols-2 gap-1 text-sm ${docsColors.text.body}`}>
                      {type.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <span className={`${docsColors.text.subtle} mr-2`}>•</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    href={type.href}
                    className={`inline-flex items-center ${docsColors.link.primary} font-medium cursor-pointer`}
                  >
                    Learn more →
                  </Link>
                </div>
              </div>
            </DocCard>
          ))}
        </div>
      </DocSection>

      {/* Next Steps */}
      <DocSection>
        <DocAlert variant="info" className="p-6">
          <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
            Not Sure Which Monitor Type to Use?
          </h2>
          <div className={`space-y-3 ${docsColors.text.body}`}>
            <p>
              <strong className={docsColors.text.heading}>For websites and APIs:</strong> Use
              HTTP/HTTPS monitoring for comprehensive checks including response validation and SSL
              certificate monitoring
            </p>
            <p>
              <strong className={docsColors.text.heading}>For databases and services:</strong> Use
              TCP monitoring to ensure ports are accessible
            </p>
            <p>
              <strong className={docsColors.text.heading}>For server availability:</strong> Use Ping
              monitoring for basic connectivity checks
            </p>
          </div>
        </DocAlert>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/getting-started", text: "Getting Started" }}
        next={{ href: "/docs/monitors/http", text: "HTTP/HTTPS Monitoring" }}
      />
    </DocLayout>
  );
}
